package com.havn.app.features.properties.presentation

import com.havn.app.core.billing.HavnPlanLimits
import com.havn.app.core.billing.HavnPlans
import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.presentation.havnFriendlyMessage
import com.havn.app.core.media.HavnPickedPhoto
import com.havn.app.core.settings.HavnUnitPreferences
import com.havn.app.core.settings.HavnUnitPreferencesRepository
import com.havn.app.core.settings.toDisplayArea
import com.havn.app.core.settings.toStoredSquareFeet
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.agency.domain.AgencyRepository
import com.havn.app.features.agency.domain.AgencyMember
import com.havn.app.features.agency.domain.AgencyMemberStatus
import com.havn.app.features.agency.domain.HavnSubscriptionTier
import com.havn.app.features.clients.domain.Client
import com.havn.app.features.clients.domain.ClientRepository
import com.havn.app.features.leads.domain.Lead
import com.havn.app.features.leads.domain.LeadRepository
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.features.properties.domain.Property
import com.havn.app.features.properties.domain.PropertyDraft
import com.havn.app.features.properties.domain.PropertyPhoto
import com.havn.app.features.properties.domain.PropertyPhotoUpload
import com.havn.app.features.properties.domain.PropertyRepository
import com.havn.app.features.properties.domain.PropertyStatus
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class PropertiesViewModel(
    private val propertyRepository: PropertyRepository,
    private val clientRepository: ClientRepository,
    private val leadRepository: LeadRepository,
    private val authRepository: AuthRepository,
    private val unitPreferencesRepository: HavnUnitPreferencesRepository,
    private val agencyRepository: AgencyRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(PropertiesUiState())
    val uiState: StateFlow<PropertiesUiState> = _uiState.asStateFlow()

    private var agentId: String? = null
    private var agentEmail: String? = null
    private var unitPreferences: HavnUnitPreferences = unitPreferencesRepository.preferences.value
    private var planLimits: HavnPlanLimits = HavnPlans.freeLimits
    private var onPropertyCreated: (() -> Unit)? = null
    private var selectedAgencyId: String? = null
    private var actAsAgent = false
    private var hasLoadedAgencyContext = false
    private val warmedPropertyPhotoIds = mutableSetOf<String>()

    init {
        scope.launch {
            unitPreferencesRepository.preferences.collect { preferences ->
                unitPreferences = preferences
                _uiState.value = _uiState.value.copy(unitPreferences = preferences)
            }
        }
        scope.launch {
            authRepository.authState.collect { state ->
                val nextAgentId = (state as? AuthState.LoggedIn)?.user?.id
                agentEmail = (state as? AuthState.LoggedIn)?.user?.email
                agentId = nextAgentId
                if (nextAgentId == null) {
                    hasLoadedAgencyContext = false
                    _uiState.value = PropertiesUiState()
                } else {
                    _uiState.value = _uiState.value.copy(currentAgentId = nextAgentId)
                    propertyRepository.ensureAgentProfile(nextAgentId, agentEmail)
                        .onFailure { setError(it) }
                    loadPlanLimits(nextAgentId)
                    if (!hasLoadedAgencyContext) {
                        hasLoadedAgencyContext = true
                        selectedAgencyId = null
                    }
                    loadAgencyMembers(selectedAgencyId)
                    loadProperties(nextAgentId)
                    loadOwnerContacts(nextAgentId)
                }
            }
        }
    }

    fun refresh() {
        val currentAgentId = agentId ?: return
        scope.launch {
            loadProperties(currentAgentId)
            loadOwnerContacts(currentAgentId)
            _uiState.value.selectedPropertyId?.let { loadPropertyInquiries(it) }
        }
    }

    fun refreshEntitlement(tier: HavnSubscriptionTier) {
        val currentAgentId = agentId ?: return
        planLimits = HavnPlans.limitsFor(tier)
        _uiState.value = _uiState.value.copy(subscriptionTier = tier)
        scope.launch {
            loadProperties(currentAgentId)
            loadOwnerContacts(currentAgentId)
        }
    }

    fun updateSearchQuery(query: String) {
        if (_uiState.value.searchQuery == query) return
        _uiState.value = _uiState.value.copy(searchQuery = query, error = null)
        val currentAgentId = agentId ?: return
        scope.launch { loadProperties(currentAgentId) }
    }

    fun loadMoreProperties() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.isLoading || state.isLoadingMore || !state.hasMoreProperties) return
        scope.launch { loadProperties(currentAgentId, append = true) }
    }

    fun setAgencyContext(agencyId: String?, nextActAsAgent: Boolean = false) {
        if (hasLoadedAgencyContext && selectedAgencyId == agencyId && actAsAgent == nextActAsAgent) return
        selectedAgencyId = agencyId
        actAsAgent = nextActAsAgent
        hasLoadedAgencyContext = true
        val currentAgentId = agentId ?: return
        scope.launch {
            loadAgencyMembers(agencyId)
            loadProperties(currentAgentId)
            loadOwnerContacts(currentAgentId)
        }
    }

    fun onPropertyCreated(callback: () -> Unit) {
        onPropertyCreated = callback
    }

    fun openProperty(propertyId: String) {
        val currentAgentId = agentId ?: return
        _uiState.value = _uiState.value.copy(
            mode = PropertiesMode.Detail,
            selectedPropertyId = propertyId,
            isDetailLoading = true,
            error = null
        )
        scope.launch {
            propertyRepository.propertyDetail(currentAgentId, propertyId)
                .onSuccess { property ->
                    replaceProperty(property)
                    _uiState.value = _uiState.value.copy(isDetailLoading = false)
                    launch { loadPropertyAssignments(propertyId) }
                    launch { loadPropertyInquiries(propertyId) }
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isDetailLoading = false)
                    setError(it)
                }
        }
    }

    fun warmPropertyPhotos(propertyId: String) {
        val currentAgentId = agentId ?: return
        if (!warmedPropertyPhotoIds.add(propertyId)) return
        scope.launch {
            propertyRepository.propertyDetail(currentAgentId, propertyId)
                .onSuccess { property -> replaceProperty(property) }
                .onFailure {
                    warmedPropertyPhotoIds.remove(propertyId)
                    HavnLogger.error("PropertiesViewModel", "Failed to warm property photos for Today", it)
                }
        }
    }

    fun closeDetail() {
        _uiState.value = _uiState.value.copy(
            mode = PropertiesMode.List,
            selectedPropertyId = null,
            selectedInquiryId = null
        )
    }

    fun openInquiries() {
        val propertyId = _uiState.value.selectedPropertyId ?: return
        _uiState.value = _uiState.value.copy(mode = PropertiesMode.Inquiries, selectedInquiryId = null, error = null)
        scope.launch { loadPropertyInquiries(propertyId) }
    }

    fun closeInquiries() {
        _uiState.value = _uiState.value.copy(mode = PropertiesMode.Detail, selectedInquiryId = null)
    }

    fun openInquiry(inquiryId: String) {
        _uiState.value = _uiState.value.copy(mode = PropertiesMode.InquiryDetail, selectedInquiryId = inquiryId, error = null)
    }

    fun closeInquiryDetail() {
        _uiState.value = _uiState.value.copy(mode = PropertiesMode.Inquiries, selectedInquiryId = null)
    }

    fun refreshSelectedInquiry() {
        val propertyId = _uiState.value.selectedPropertyId ?: return
        scope.launch { loadPropertyInquiries(propertyId) }
    }

    fun setInquiryTemperature(leadId: String, temperature: LeadTemperature) {
        val currentAgentId = agentId ?: return
        val currentInquiry = _uiState.value.propertyInquiries.firstOrNull { it.id == leadId && it.isInquiry } ?: return
        if (currentInquiry.temperature == temperature) return

        replaceInquiry(currentInquiry.copy(temperature = temperature))
        _uiState.value = _uiState.value.copy(isSavingInquiry = true, error = null)
        scope.launch {
            leadRepository.updateLeadTemperature(currentAgentId, leadId, temperature)
                .onSuccess {
                    _uiState.value = _uiState.value.copy(isSavingInquiry = false)
                }
                .onFailure {
                    replaceInquiry(currentInquiry)
                    _uiState.value = _uiState.value.copy(isSavingInquiry = false)
                    setError(it)
                }
        }
    }

    fun startAdd() {
        val state = _uiState.value
        if (state.totalPropertyCount >= planLimits.maxProperties) {
            _uiState.value = state.copy(
                mode = PropertiesMode.List,
                error = "Your current plan includes up to ${planLimits.maxProperties} properties. Upgrade to add more."
            )
            return
        }
        _uiState.value = _uiState.value.copy(
            mode = PropertiesMode.Add,
            selectedPropertyId = null,
            form = PropertyFormState()
        )
    }

    fun startEdit(propertyId: String) {
        val property = _uiState.value.properties.firstOrNull { it.id == propertyId } ?: return
        _uiState.value = _uiState.value.copy(
            mode = PropertiesMode.Edit,
            selectedPropertyId = propertyId,
            form = property.toFormState(unitPreferences)
        )
    }

    fun cancelForm() {
        val selectedId = _uiState.value.selectedPropertyId
        _uiState.value = _uiState.value.copy(
            mode = if (selectedId == null) PropertiesMode.List else PropertiesMode.Detail,
            form = PropertyFormState()
        )
    }

    fun startLocateProperty() {
        if (_uiState.value.mode != PropertiesMode.Edit) return
        _uiState.value = _uiState.value.copy(mode = PropertiesMode.Locate, error = null)
    }

    fun cancelLocateProperty() {
        _uiState.value = _uiState.value.copy(mode = PropertiesMode.Edit)
    }

    fun updatePendingLocation(latitude: Double, longitude: Double) {
        _uiState.value = _uiState.value.copy(
            form = _uiState.value.form.copy(latitude = latitude, longitude = longitude),
            error = null
        )
    }

    fun updateForm(transform: PropertyFormState.() -> PropertyFormState) {
        _uiState.value = _uiState.value.copy(form = _uiState.value.form.transform(), error = null)
    }

    fun showAddInquiry() {
        if (_uiState.value.selectedPropertyId == null) return
        _uiState.value = _uiState.value.copy(
            mode = PropertiesMode.AddInquiry,
            inquiryForm = PropertyInquiryFormState(),
            inquiryContactQuery = "",
            error = null
        )
        agentId?.let { currentAgentId ->
            scope.launch { loadOwnerContacts(currentAgentId) }
        }
    }

    fun closeAddInquiry() {
        _uiState.value = _uiState.value.copy(
            mode = PropertiesMode.Inquiries,
            inquiryForm = PropertyInquiryFormState(),
            inquiryContactQuery = "",
            isSavingInquiry = false
        )
    }

    fun updateInquiryForm(transform: PropertyInquiryFormState.() -> PropertyInquiryFormState) {
        _uiState.value = _uiState.value.copy(inquiryForm = _uiState.value.inquiryForm.transform(), error = null)
    }

    fun updateInquiryContactQuery(query: String) {
        if (_uiState.value.inquiryContactQuery == query) return
        _uiState.value = _uiState.value.copy(inquiryContactQuery = query)
        val currentAgentId = agentId ?: return
        scope.launch { loadOwnerContacts(currentAgentId, query = query) }
    }

    fun saveInquiry() {
        val propertyId = _uiState.value.selectedPropertyId ?: return
        val form = _uiState.value.inquiryForm
        val contactId = form.contactId
        if (contactId == null) {
            _uiState.value = _uiState.value.copy(
                inquiryForm = form.copy(validationError = "Choose a contact first.")
            )
            return
        }
        _uiState.value = _uiState.value.copy(isSavingInquiry = true, error = null)
        scope.launch {
            leadRepository.createInquiryLead(
                agentId = agentId ?: return@launch,
                propertyId = propertyId,
                contactId = contactId,
                temperature = form.temperature,
                message = null,
                notes = null,
                source = form.source
            ).onSuccess { inquiryLead ->
                _uiState.value = _uiState.value.copy(
                    propertyInquiries = (listOf(inquiryLead) + _uiState.value.propertyInquiries.filterNot { it.id == inquiryLead.id })
                        .sortedByDescending { it.updatedAt ?: it.createdAt.orEmpty() },
                    inquiryForm = PropertyInquiryFormState(),
                    inquiryContactQuery = "",
                    isSavingInquiry = false,
                    mode = PropertiesMode.Inquiries
                )
            }.onFailure {
                _uiState.value = _uiState.value.copy(isSavingInquiry = false)
                setError(it)
            }
        }
    }

    fun addInquiryContact(contactId: String) {
        _uiState.value = _uiState.value.copy(
            inquiryForm = _uiState.value.inquiryForm.copy(contactId = contactId, validationError = null)
        )
        saveInquiry()
    }

    fun requestDeleteInquiry(leadId: String) {
        val inquiry = _uiState.value.propertyInquiries.firstOrNull { it.id == leadId && it.isInquiry } ?: return
        _uiState.value = _uiState.value.copy(pendingDeleteInquiryId = inquiry.id, error = null)
    }

    fun dismissDeleteInquiryConfirmation() {
        _uiState.value = _uiState.value.copy(pendingDeleteInquiryId = null)
    }

    fun confirmDeleteInquiry() {
        val leadId = _uiState.value.pendingDeleteInquiryId ?: return
        val currentAgentId = agentId ?: return
        _uiState.value = _uiState.value.copy(isSavingInquiry = true, error = null)
        scope.launch {
            leadRepository.deleteInquiryLead(currentAgentId, leadId)
                .onSuccess {
                    _uiState.value = _uiState.value.copy(
                        propertyInquiries = _uiState.value.propertyInquiries.filterNot { it.id == leadId },
                        pendingDeleteInquiryId = null,
                        selectedInquiryId = _uiState.value.selectedInquiryId.takeIf { it != leadId },
                        isSavingInquiry = false
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSavingInquiry = false)
                    setError(it)
                }
        }
    }

    fun addPendingPhoto(photo: HavnPickedPhoto?) {
        if (photo == null || photo.bytes.isEmpty()) {
            _uiState.value = _uiState.value.copy(error = "Choose a valid property photo.")
            return
        }
        _uiState.value = _uiState.value.copy(
            form = _uiState.value.form.copy(pendingPhotos = _uiState.value.form.pendingPhotos + photo),
            error = null
        )
    }

    fun removePendingPhoto(index: Int) {
        val photos = _uiState.value.form.pendingPhotos
        if (index !in photos.indices) return
        _uiState.value = _uiState.value.copy(
            form = _uiState.value.form.copy(pendingPhotos = photos.filterIndexed { photoIndex, _ -> photoIndex != index }),
            error = null
        )
    }

    fun saveProperty() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.mode == PropertiesMode.Add && state.totalPropertyCount >= planLimits.maxProperties) {
            _uiState.value = state.copy(
                isSaving = false,
                error = "Your current plan includes up to ${planLimits.maxProperties} properties. Upgrade to add more."
            )
            return
        }
        val validation = state.form.validate()
        if (validation != null) {
            _uiState.value = state.copy(form = state.form.copy(validationError = validation))
            return
        }

        _uiState.value = state.copy(isSaving = true, error = null)
        scope.launch {
            val draft = state.form.toDraft(unitPreferences)
            val result = when (state.mode) {
                PropertiesMode.Add -> propertyRepository.addProperty(currentAgentId, draft, selectedAgencyId)
                PropertiesMode.Edit -> {
                    val propertyId = state.selectedPropertyId ?: return@launch
                    propertyRepository.updateProperty(currentAgentId, propertyId, draft)
                }
                else -> return@launch
            }

            result
                .onSuccess { property ->
                    var savedProperty = property
                    if (state.mode == PropertiesMode.Add) {
                        val assignmentIds = state.form.assignedAgentIds.distinct()
                        if (state.isProPlan && assignmentIds.isNotEmpty()) {
                            propertyRepository.assignPropertyAccess(property.id, assignmentIds)
                                .onFailure { HavnLogger.error("PropertiesViewModel", "Failed to assign new property access", it) }
                        }
                        state.form.pendingPhotos.forEachIndexed { index, pendingPhoto ->
                            propertyRepository.uploadPropertyPhoto(
                                agentId = currentAgentId,
                                propertyId = property.id,
                                upload = pendingPhoto.toPropertyPhotoUpload(isHero = index == 0)
                            ).onFailure {
                                _uiState.value = _uiState.value.copy(isSaving = false)
                                setError(it)
                                return@launch
                            }
                        }
                        if (state.form.pendingPhotos.isNotEmpty()) {
                            savedProperty = propertyRepository.propertyDetail(currentAgentId, property.id).getOrElse { property }
                        }
                        onPropertyCreated?.invoke()
                    }
                    if (state.mode == PropertiesMode.Edit) {
                        val originalOrder = state.selectedProperty?.photos.orEmpty().map { it.id }
                        val draftOrder = state.form.photoOrderIds
                        if (draftOrder.isNotEmpty() && draftOrder != originalOrder) {
                            propertyRepository.reorderPropertyPhotos(currentAgentId, property.id, draftOrder)
                                .onFailure {
                                    _uiState.value = _uiState.value.copy(isSaving = false)
                                    setError(it)
                                    return@launch
                                }
                            savedProperty = propertyRepository.propertyDetail(currentAgentId, property.id).getOrElse { property }
                        }
                    }
                    replaceProperty(savedProperty)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        mode = PropertiesMode.Detail,
                        selectedPropertyId = savedProperty.id,
                        form = PropertyFormState(),
                        selectedPropertyAssignmentIds = if (state.mode == PropertiesMode.Add) {
                            state.form.assignedAgentIds.distinct()
                        } else {
                            _uiState.value.selectedPropertyAssignmentIds
                        }
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun archiveSelected() {
        val currentAgentId = agentId ?: return
        val propertyId = _uiState.value.selectedPropertyId ?: return
        _uiState.value = _uiState.value.copy(isSaving = true, error = null)
        scope.launch {
            propertyRepository.archiveProperty(currentAgentId, propertyId)
                .onSuccess {
                    refreshAfterMutation(currentAgentId, propertyId)
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun requestDeleteSelected() {
        if (_uiState.value.selectedPropertyId == null) return
        _uiState.value = _uiState.value.copy(showDeleteConfirmation = true, error = null)
    }

    fun dismissDeleteConfirmation() {
        _uiState.value = _uiState.value.copy(showDeleteConfirmation = false)
    }

    fun confirmDeleteSelected() {
        val currentAgentId = agentId ?: return
        val propertyId = _uiState.value.selectedPropertyId ?: return
        _uiState.value = _uiState.value.copy(isSaving = true, showDeleteConfirmation = false, error = null)
        scope.launch {
            propertyRepository.deleteProperty(currentAgentId, propertyId)
                .onSuccess {
                    _uiState.value = _uiState.value.copy(
                        properties = _uiState.value.properties.filterNot { it.id == propertyId },
                        totalPropertyCount = (_uiState.value.totalPropertyCount - 1).coerceAtLeast(0),
                        selectedPropertyId = null,
                        mode = PropertiesMode.List,
                        isSaving = false
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun uploadHeroPhoto(photo: HavnPickedPhoto?) {
        if (photo == null) {
            _uiState.value = _uiState.value.copy(error = "Photo selection was cancelled or unavailable on this device.")
            return
        }
        val currentAgentId = agentId ?: return
        val propertyId = _uiState.value.selectedPropertyId ?: return
        val shouldBeHero = _uiState.value.selectedProperty?.photos.isNullOrEmpty()
        _uiState.value = _uiState.value.copy(isUploadingPhoto = true, error = null)
        scope.launch {
            propertyRepository.uploadPropertyPhoto(
                agentId = currentAgentId,
                propertyId = propertyId,
                upload = PropertyPhotoUpload(
                    fileName = photo.fileName,
                    bytes = photo.bytes,
                    contentType = photo.contentType,
                    altText = "HAVN property hero photo",
                    isHero = shouldBeHero,
                    thumbnailDataUrl = photo.thumbnailDataUrl,
                    thumbnailWidth = photo.thumbnailWidth,
                    thumbnailHeight = photo.thumbnailHeight,
                    listThumbnailDataUrl = photo.listThumbnailDataUrl,
                    listThumbnailWidth = photo.listThumbnailWidth,
                    listThumbnailHeight = photo.listThumbnailHeight
                )
            ).onSuccess {
                propertyRepository.propertyDetail(currentAgentId, propertyId)
                    .onSuccess { property -> replaceProperty(property) }
                    .onFailure { setError(it) }
                _uiState.value = _uiState.value.copy(isUploadingPhoto = false)
            }.onFailure {
                _uiState.value = _uiState.value.copy(isUploadingPhoto = false)
                setError(it)
            }
        }
    }

    fun requestDeletePhoto(photoId: String) {
        _uiState.value = _uiState.value.copy(pendingDeletePhotoId = photoId, error = null)
    }

    fun dismissDeletePhotoConfirmation() {
        _uiState.value = _uiState.value.copy(pendingDeletePhotoId = null)
    }

    fun confirmDeletePhoto() {
        val currentAgentId = agentId ?: return
        val propertyId = _uiState.value.selectedPropertyId ?: return
        val photoId = _uiState.value.pendingDeletePhotoId ?: return
        _uiState.value = _uiState.value.copy(isSaving = true, pendingDeletePhotoId = null, error = null)
        scope.launch {
            propertyRepository.deletePropertyPhoto(currentAgentId, propertyId, photoId)
                .onSuccess {
                    propertyRepository.propertyDetail(currentAgentId, propertyId)
                        .onSuccess { property -> replaceProperty(property) }
                        .onFailure { setError(it) }
                    _uiState.value = _uiState.value.copy(isSaving = false)
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun movePhoto(photoId: String, direction: Int) {
        val ids = currentDraftPhotoOrder()
        val currentIndex = ids.indexOf(photoId)
        val targetIndex = currentIndex + direction
        if (currentIndex !in ids.indices || targetIndex !in ids.indices) return
        updateDraftPhotoOrder(ids.reordered(currentIndex, targetIndex))
    }

    fun reorderPhoto(photoId: String, targetIndex: Int) {
        val ids = currentDraftPhotoOrder()
        val currentIndex = ids.indexOf(photoId)
        if (currentIndex !in ids.indices) return
        val clampedTargetIndex = targetIndex.coerceIn(ids.indices)
        if (currentIndex == clampedTargetIndex) return
        updateDraftPhotoOrder(ids.reordered(currentIndex, clampedTargetIndex))
    }

    fun makePhotoMain(photoId: String) {
        val ids = currentDraftPhotoOrder()
        if (photoId !in ids) return
        updateDraftPhotoOrder(listOf(photoId) + ids.filterNot { it == photoId })
    }

    private fun currentDraftPhotoOrder(): List<String> {
        val state = _uiState.value
        return state.form.photoOrderIds.takeIf { it.isNotEmpty() }
            ?: state.selectedProperty?.photos.orEmpty().map { it.id }
    }

    private fun updateDraftPhotoOrder(photoIds: List<String>) {
        _uiState.value = _uiState.value.copy(
            form = _uiState.value.form.copy(photoOrderIds = photoIds),
            error = null
        )
    }

    fun assignSelectedPropertyToTeam(agentIds: List<String>) {
        val propertyId = _uiState.value.selectedPropertyId ?: return
        _uiState.value = _uiState.value.copy(isAssigningProperty = true, error = null)
        scope.launch {
            propertyRepository.assignPropertyAccess(propertyId, agentIds)
                .onSuccess {
                    _uiState.value = _uiState.value.copy(
                        isAssigningProperty = false,
                        selectedPropertyAssignmentIds = agentIds.distinct()
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isAssigningProperty = false)
                    setError(it)
                }
        }
    }

    fun updateSelectedPropertyOwner(contactId: String?) {
        val currentAgentId = agentId ?: return
        val property = _uiState.value.selectedProperty ?: return
        val ownerContact = contactId?.let { selectedId ->
            _uiState.value.ownerContacts.firstOrNull { it.id == selectedId }
        }
        val draft = property.toFormState(unitPreferences).copy(
            ownerContactId = contactId,
            ownerFirstName = ownerContact?.firstName.orEmpty(),
            ownerLastName = ownerContact?.lastName.orEmpty(),
            ownerEmail = ownerContact?.email.orEmpty(),
            ownerPhone = ownerContact?.phone.orEmpty()
        ).toDraft(unitPreferences)

        _uiState.value = _uiState.value.copy(isSaving = true, error = null)
        scope.launch {
            propertyRepository.updateProperty(currentAgentId, property.id, draft)
                .onSuccess { updatedProperty ->
                    replaceProperty(updatedProperty)
                    _uiState.value = _uiState.value.copy(isSaving = false)
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    private fun persistPhotoOrder(currentAgentId: String, propertyId: String, orderedPhotoIds: List<String>) {
        _uiState.value = _uiState.value.copy(isSaving = true, error = null)
        scope.launch {
            propertyRepository.reorderPropertyPhotos(currentAgentId, propertyId, orderedPhotoIds)
                .onSuccess {
                    propertyRepository.propertyDetail(currentAgentId, propertyId)
                        .onSuccess { property -> replaceProperty(property) }
                        .onFailure { setError(it) }
                    _uiState.value = _uiState.value.copy(isSaving = false)
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    fun dispose() {
        scope.cancel()
    }

    private suspend fun loadProperties(currentAgentId: String, append: Boolean = false) {
        val state = _uiState.value
        val offset = if (append) state.properties.size else 0
        _uiState.value = if (append) {
            state.copy(isLoadingMore = true, error = null)
        } else {
            state.copy(isLoading = true, error = null)
        }
        propertyRepository.propertyPage(
            agentId = currentAgentId,
            agencyId = selectedAgencyId,
            query = _uiState.value.searchQuery,
            offset = offset,
            limit = PROPERTY_PAGE_SIZE,
            actAsAgent = actAsAgent
        )
            .onSuccess { properties ->
                val cappedProperties = properties.map { it.visibleForPlan() }
                val merged = if (append) (_uiState.value.properties + cappedProperties).distinctBy { it.id } else cappedProperties
                val visibleProperties = merged.take(planLimits.visibleProperties)
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    isLoadingMore = false,
                    hasLoadedInitialPage = _uiState.value.hasLoadedInitialPage || !append,
                    properties = visibleProperties,
                    totalPropertyCount = visibleProperties.size,
                    hasMoreProperties = properties.size == PROPERTY_PAGE_SIZE && visibleProperties.size < planLimits.visibleProperties,
                    selectedPropertyId = _uiState.value.selectedPropertyId?.takeIf { selectedId ->
                        visibleProperties.any { it.id == selectedId }
                    }
                )
            }
            .onFailure {
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    isLoadingMore = false,
                    hasLoadedInitialPage = _uiState.value.hasLoadedInitialPage || !append
                )
                setError(it)
            }
    }

    private suspend fun loadAgencyMembers(agencyId: String?) {
        if (agencyId == null) {
            _uiState.value = _uiState.value.copy(agencyMembers = emptyList())
            return
        }
        agencyRepository.membersForAgency(agencyId)
            .onSuccess { members ->
                _uiState.value = _uiState.value.copy(
                    agencyMembers = members.filter { it.status == AgencyMemberStatus.Active && it.agentId != null }
                )
            }
            .onFailure {
                HavnLogger.error("PropertiesViewModel", "Failed to load agency members", it)
            }
    }

    private suspend fun loadPropertyAssignments(propertyId: String) {
        propertyRepository.propertyAssignmentAgentIds(propertyId)
            .onSuccess { assignments ->
                _uiState.value = _uiState.value.copy(selectedPropertyAssignmentIds = assignments)
            }
            .onFailure {
                HavnLogger.error("PropertiesViewModel", "Failed to load property assignments", it)
            }
    }

    private suspend fun loadOwnerContacts(currentAgentId: String, query: String = "") {
        clientRepository.clientPage(currentAgentId, selectedAgencyId, query = query.trim(), offset = 0, limit = OWNER_CONTACT_PAGE_SIZE)
            .onSuccess { contacts ->
                _uiState.value = _uiState.value.copy(ownerContacts = contacts)
            }
            .onFailure {
                HavnLogger.error("PropertiesViewModel", "Failed to load owner contacts", it)
            }
    }

    private suspend fun loadPropertyInquiries(propertyId: String) {
        _uiState.value = _uiState.value.copy(isLoadingInquiries = true)
        val currentAgentId = agentId ?: return
        leadRepository.inquiryLeadsForProperty(agentId = currentAgentId, propertyId = propertyId, limit = PROPERTY_INQUIRY_PAGE_SIZE)
            .onSuccess { inquiries ->
                _uiState.value = _uiState.value.copy(
                    propertyInquiries = _uiState.value.propertyInquiries.filterNot { it.propertyId == propertyId } + inquiries,
                    isLoadingInquiries = false
                )
            }
            .onFailure {
                _uiState.value = _uiState.value.copy(isLoadingInquiries = false)
                HavnLogger.error("PropertiesViewModel", "Failed to load property inquiries", it)
            }
    }

    private fun replaceInquiry(inquiry: Lead) {
        _uiState.value = _uiState.value.copy(
            propertyInquiries = _uiState.value.propertyInquiries
                .map { if (it.id == inquiry.id) inquiry else it }
                .sortedByDescending { it.updatedAt ?: it.createdAt ?: it.checkedInAt }
        )
    }

    private fun replaceProperty(property: Property) {
        val visibleProperty = property.visibleForPlan()
        val existing = _uiState.value.properties
        val updated = if (existing.any { it.id == visibleProperty.id }) {
            existing.map { if (it.id == visibleProperty.id) visibleProperty else it }
        } else {
            listOf(visibleProperty) + existing.take(planLimits.visibleProperties - 1)
        }.take(planLimits.visibleProperties)
        val total = if (existing.any { it.id == visibleProperty.id }) {
            _uiState.value.totalPropertyCount
        } else {
            (_uiState.value.totalPropertyCount + 1).coerceAtMost(planLimits.maxProperties)
        }
        _uiState.value = _uiState.value.copy(properties = updated, totalPropertyCount = total)
    }

    private fun Property.visibleForPlan(): Property {
        val maxPhotos = planLimits.maxPhotosPerProperty.coerceAtLeast(0)
        if (photos.size <= maxPhotos) return this
        return copy(photos = photos.planVisiblePhotos(maxPhotos, heroPhotoPath))
    }

    private suspend fun loadPlanLimits(currentAgentId: String) {
        agencyRepository.entitlementForUser(currentAgentId)
            .onSuccess { entitlement ->
                planLimits = HavnPlans.limitsFor(entitlement.tier)
                _uiState.value = _uiState.value.copy(subscriptionTier = entitlement.tier)
            }
            .onFailure {
                HavnLogger.error("PropertiesViewModel", "Failed to load plan limits", it)
                planLimits = HavnPlans.freeLimits
                _uiState.value = _uiState.value.copy(subscriptionTier = HavnSubscriptionTier.Free)
            }
    }

    private suspend fun refreshAfterMutation(currentAgentId: String, propertyId: String) {
        propertyRepository.propertyDetail(currentAgentId, propertyId)
            .onSuccess { property ->
                replaceProperty(property)
                _uiState.value = _uiState.value.copy(isSaving = false)
            }
            .onFailure {
                _uiState.value = _uiState.value.copy(isSaving = false)
                loadProperties(currentAgentId)
            }
    }

    private fun setError(error: Throwable) {
        HavnLogger.error("PropertiesViewModel", "User-visible properties error", error)
        _uiState.value = _uiState.value.copy(
            error = error.havnFriendlyMessage("Could not update properties. Please try again.")
            )
        }
    }

data class PropertiesUiState(
    val isLoading: Boolean = false,
    val isLoadingMore: Boolean = false,
    val hasLoadedInitialPage: Boolean = false,
    val isDetailLoading: Boolean = false,
    val isSaving: Boolean = false,
    val isUploadingPhoto: Boolean = false,
    val error: String? = null,
    val properties: List<Property> = emptyList(),
    val searchQuery: String = "",
    val hasMoreProperties: Boolean = false,
    val ownerContacts: List<Client> = emptyList(),
    val propertyInquiries: List<Lead> = emptyList(),
    val isLoadingInquiries: Boolean = false,
    val isSavingInquiry: Boolean = false,
    val inquiryForm: PropertyInquiryFormState = PropertyInquiryFormState(),
    val inquiryContactQuery: String = "",
    val selectedInquiryId: String? = null,
    val pendingDeleteInquiryId: String? = null,
    val totalPropertyCount: Int = 0,
    val selectedPropertyId: String? = null,
    val mode: PropertiesMode = PropertiesMode.List,
    val form: PropertyFormState = PropertyFormState(),
    val showDeleteConfirmation: Boolean = false,
    val pendingDeletePhotoId: String? = null,
    val unitPreferences: HavnUnitPreferences = HavnUnitPreferences(),
    val currentAgentId: String? = null,
    val subscriptionTier: HavnSubscriptionTier = HavnSubscriptionTier.Free,
    val agencyMembers: List<AgencyMember> = emptyList(),
    val selectedPropertyAssignmentIds: List<String> = emptyList(),
    val isAssigningProperty: Boolean = false
) {
    val isProPlan: Boolean
        get() = subscriptionTier == HavnSubscriptionTier.ProAgent || subscriptionTier == HavnSubscriptionTier.TeamAgent

    val selectedProperty: Property?
        get() = properties.firstOrNull { it.id == selectedPropertyId }

    val selectedPropertyInquiries: List<Lead>
        get() = selectedPropertyId?.let { propertyId ->
            propertyInquiries.filter { it.propertyId == propertyId }
        }.orEmpty()

    val selectedInquiry: Lead?
        get() = propertyInquiries.firstOrNull { it.id == selectedInquiryId }

    val activeCount: Int
        get() = properties.count { it.status == PropertyStatus.Active }

    val plannedCount: Int
        get() = properties.count { it.status == PropertyStatus.Planned }

    val archivedCount: Int
        get() = properties.count { it.status == PropertyStatus.Archived }

    val totalVisitors: Int
        get() = properties.sumOf { it.stats.totalVisitors }

}

private const val PROPERTY_PAGE_SIZE = 20
private const val PROPERTY_INQUIRY_PAGE_SIZE = 20
private const val OWNER_CONTACT_PAGE_SIZE = 50

enum class PropertiesMode {
    List,
    Detail,
    Inquiries,
    InquiryDetail,
    AddInquiry,
    Add,
    Edit,
    Locate
}

data class PropertyInquiryFormState(
    val contactId: String? = null,
    val temperature: LeadTemperature = LeadTemperature.Warm,
    val source: String = "",
    val validationError: String? = null
)

data class PropertyFormState(
    val title: String = "",
    val addressLine1: String = "",
    val addressLine2: String = "",
    val city: String = "",
    val region: String = "",
    val postalCode: String = "",
    val country: String = "",
    val latitude: Double? = null,
    val longitude: Double? = null,
    val askingPrice: String = "",
    val bedrooms: String = "",
    val bathrooms: String = "",
    val interiorSqft: String = "",
    val status: PropertyStatus = PropertyStatus.Planned,
    val description: String = "",
    val ownerContactId: String? = null,
    val ownerFirstName: String = "",
    val ownerLastName: String = "",
    val ownerEmail: String = "",
    val ownerPhone: String = "",
    val assignedAgentIds: List<String> = emptyList(),
    val pendingPhotos: List<HavnPickedPhoto> = emptyList(),
    val photoOrderIds: List<String> = emptyList(),
    val validationError: String? = null
) {
    fun toDraft(unitPreferences: HavnUnitPreferences): PropertyDraft = PropertyDraft(
        title = title,
        addressLine1 = addressLine1,
        addressLine2 = addressLine2,
        city = city,
        region = region,
        postalCode = postalCode,
        country = country,
        latitude = latitude,
        longitude = longitude,
        askingPrice = askingPrice.digitsOnly().toLongOrNull() ?: 0,
        bedrooms = bedrooms.toDoubleOrNull(),
        bathrooms = bathrooms.toDoubleOrNull(),
        interiorSqft = interiorSqft.digitsOnly().toIntOrNull()?.toStoredSquareFeet(unitPreferences),
        status = status,
        description = description,
        ownerContactId = ownerContactId,
        ownerFirstName = ownerFirstName,
        ownerLastName = ownerLastName,
        ownerEmail = ownerEmail,
        ownerPhone = ownerPhone
    )

    fun validate(): String? = when {
        title.isBlank() -> "Property title is required."
        addressLine1.isBlank() -> "Street address is required."
        city.isBlank() -> "City is required."
        region.isBlank() -> "State or region is required."
        postalCode.isBlank() -> "Postal code is required."
        askingPrice.digitsOnly().toLongOrNull() == null -> "Enter a valid asking price."
        else -> null
    }
}

private fun HavnPickedPhoto.toPropertyPhotoUpload(isHero: Boolean): PropertyPhotoUpload = PropertyPhotoUpload(
    fileName = fileName,
    bytes = bytes,
    contentType = contentType,
    altText = "HAVN property photo",
    isHero = isHero,
    thumbnailDataUrl = thumbnailDataUrl,
    thumbnailWidth = thumbnailWidth,
    thumbnailHeight = thumbnailHeight,
    listThumbnailDataUrl = listThumbnailDataUrl,
    listThumbnailWidth = listThumbnailWidth,
    listThumbnailHeight = listThumbnailHeight
)

private fun Property.toFormState(unitPreferences: HavnUnitPreferences): PropertyFormState = PropertyFormState(
    title = title,
    addressLine1 = addressLine1,
    addressLine2 = addressLine2.orEmpty(),
    city = city,
    region = region,
    postalCode = postalCode,
    country = country,
    latitude = latitude,
    longitude = longitude,
    askingPrice = askingPrice.toString(),
    bedrooms = bedrooms?.toCleanString().orEmpty(),
    bathrooms = bathrooms?.toCleanString().orEmpty(),
    interiorSqft = interiorSqft?.toDisplayArea(unitPreferences)?.toString().orEmpty(),
    status = status,
    description = description.orEmpty(),
    ownerContactId = ownerContactId,
    ownerFirstName = ownerFirstName.orEmpty(),
    ownerLastName = ownerLastName.orEmpty(),
    ownerEmail = ownerEmail.orEmpty(),
    ownerPhone = ownerPhone.orEmpty(),
    photoOrderIds = photos.map { it.id }
)

private fun List<PropertyPhoto>.planVisiblePhotos(maxPhotos: Int, heroPhotoPath: String?): List<PropertyPhoto> {
    if (maxPhotos <= 0) return emptyList()
    if (size <= maxPhotos) return this
    val originalIndex = withIndex().associate { it.value.id to it.index }
    return sortedWith(
        compareByDescending<PropertyPhoto> { heroPhotoPath != null && it.storagePath == heroPhotoPath }
            .thenByDescending { it.isHero }
            .thenBy { it.sortOrder }
            .thenBy { originalIndex[it.id] ?: Int.MAX_VALUE }
    ).take(maxPhotos)
}

private fun List<String>.reordered(fromIndex: Int, toIndex: Int): List<String> =
    toMutableList().apply {
        val value = removeAt(fromIndex)
        add(toIndex, value)
    }

private fun String.digitsOnly(): String = filter { it.isDigit() }

private fun Double.toCleanString(): String {
    val intValue = toInt()
    return if (this == intValue.toDouble()) intValue.toString() else toString()
}
